/**
 * Componente ChartWrapper
 * 
 * Wrapper genérico para gráficos que gerencia estados de loading, error e empty.
 * Fornece uma interface consistente para todos os gráficos do dashboard.
 * 
 * @module components/Dashboard/ChartsSection/ChartWrapper
 */

import React from 'react';
import PropTypes from 'prop-types';
import LoadingIndicator from '../../Common/LoadingIndicator';
import Alert from '../../UI/Alert';

/**
 * Componente wrapper para gráficos
 * 
 * Gerencia estados de loading, error e empty data de forma consistente.
 * Renderiza o componente de gráfico apenas quando os dados estão disponíveis.
 * 
 * @param {object} props - Propriedades do componente
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Error|string|null} props.error - Erro ocorrido (null se não houver)
 * @param {Array} props.data - Dados para o gráfico
 * @param {React.Component} props.children - Componente de gráfico a ser renderizado
 * @param {string} [props.loadingMessage] - Mensagem de loading personalizada
 * @param {string} [props.errorMessage] - Mensagem de erro personalizada
 * @param {string} [props.emptyMessage] - Mensagem quando não há dados
 * @param {string} [props.className] - Classes CSS adicionais para o Card
 * @param {string} [props.testId] - ID para testes (data-testid)
 * 
 * @returns {JSX.Element} Componente wrapper
 * 
 * @example
 * ```jsx
 * <ChartWrapper
 *     isLoading={isLoading}
 *     error={error}
 *     data={chartData}
 *     loadingMessage="Carregando gráfico..."
 * >
 *     <GraficoPizza data={chartData} {...chartProps} />
 * </ChartWrapper>
 * ```
 */
const ChartWrapper = ({
    isLoading,
    error = null,
    data = null,
    children,
    loadingMessage = 'Carregando dados...',
    errorMessage = null,
    emptyMessage = 'Sem dados para exibir.',
    className = '',
    testId = null,
}) => {
    // Classes base do card no estilo de pacientes
    const cardBaseClasses = "bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:-translate-y-1 relative z-10 hover:z-20";

    // Estado de loading
    if (isLoading) {
        return (
            <div className={`${cardBaseClasses} p-6 ${className}`} data-testid={testId || 'chart-wrapper-loading'}>
                <LoadingIndicator size="small" message={loadingMessage} />
            </div>
        );
    }

    // Estado de erro
    if (error) {
        const message = errorMessage || (error?.message || error || 'Erro ao carregar dados do gráfico.');
        return (
            <div className={`${cardBaseClasses} p-6 ${className}`} data-testid={testId || 'chart-wrapper-error'}>
                <Alert type="error" message={message} />
            </div>
        );
    }

    // Estado vazio (sem dados)
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className={`${cardBaseClasses} p-6 ${className}`} data-testid={testId || 'chart-wrapper-empty'}>
                <div className="h-64 flex items-center justify-center">
                    <p className="text-gray-500">{emptyMessage}</p>
                </div>
            </div>
        );
    }

    // Renderiza o gráfico quando os dados estão disponíveis
    return (
        <div 
            className={`${cardBaseClasses} ${className}`}
            data-testid={testId || 'chart-wrapper'}
            role="article"
            aria-label="Gráfico de dados"
        >
            {children}
        </div>
    );
};

ChartWrapper.propTypes = {
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Erro ocorrido (null se não houver) */
    error: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Dados para o gráfico */
    data: PropTypes.array,
    
    /** Componente de gráfico a ser renderizado */
    children: PropTypes.node.isRequired,
    
    /** Mensagem de loading personalizada */
    loadingMessage: PropTypes.string,
    
    /** Mensagem de erro personalizada */
    errorMessage: PropTypes.string,
    
    /** Mensagem quando não há dados */
    emptyMessage: PropTypes.string,
    
    /** Classes CSS adicionais para o Card */
    className: PropTypes.string,
    
    /** ID para testes (data-testid) */
    testId: PropTypes.string,
};

// Default props removidos - usando default parameters na função

export default ChartWrapper;

